import { LegalDocument, type LegalSection } from '@/components/website/LegalDocument'
import { site } from '@/data/site'

const sections: LegalSection[] = [
  {
    heading: '1. Introduction',
    body: (
      <p>
        {site.name} respects your privacy and is committed to protecting your personal and health information.
        This Privacy Policy explains what information we collect through our website and online services, how
        we use it, and the choices you have.
      </p>
    ),
  },
  {
    heading: '2. Information We Collect',
    body: (
      <>
        <p>We may collect the following categories of information:</p>
        <ul>
          <li>
            <strong>Information you provide:</strong> name, phone number, email address, appointment details,
            and messages you send through our forms.
          </li>
          <li>
            <strong>Health and report information:</strong> receipt numbers and the medical reports made
            available through our secure report-check service.
          </li>
          <li>
            <strong>Technical information:</strong> IP address, browser type, device information, and pages
            visited, collected automatically when you use the website.
          </li>
        </ul>
      </>
    ),
  },
  {
    heading: '3. How We Use Your Information',
    body: (
      <>
        <p>We use your information to:</p>
        <ul>
          <li>schedule, confirm, and manage appointments;</li>
          <li>verify your identity and deliver medical reports securely (including by OTP);</li>
          <li>respond to enquiries and provide customer support;</li>
          <li>maintain, secure, and improve our website and services;</li>
          <li>comply with legal and regulatory obligations.</li>
        </ul>
      </>
    ),
  },
  {
    heading: '4. Legal Basis and Consent',
    body: (
      <p>
        We process your information with your consent, to provide services you request, to comply with the
        law, and for our legitimate interest in operating a safe and effective healthcare service. You may
        withdraw your consent at any time, subject to legal and contractual restrictions.
      </p>
    ),
  },
  {
    heading: '5. Sharing and Disclosure',
    body: (
      <p>
        We do not sell your personal information. We may share it with doctors and clinical staff involved in
        your care, service providers who help us operate the website and communications, and government or
        regulatory authorities where required by law.
      </p>
    ),
  },
  {
    heading: '6. Data Retention',
    body: (
      <p>
        We retain personal and health information only for as long as necessary to provide care, meet legal,
        medical-record, and regulatory requirements, and resolve disputes. When it is no longer needed, we
        securely delete or anonymise it.
      </p>
    ),
  },
  {
    heading: '7. Data Security',
    body: (
      <p>
        We use administrative, technical, and physical safeguards — including OTP verification for report
        access — to protect your information. No method of transmission or storage is completely secure, so we
        cannot guarantee absolute security.
      </p>
    ),
  },
  {
    heading: '8. Cookies and Tracking',
    body: (
      <p>
        Our website may use cookies and similar technologies to keep the site working, remember preferences,
        and understand how it is used. You can control or disable cookies through your browser settings, but
        some features may not work correctly without them.
      </p>
    ),
  },
  {
    heading: '9. Your Rights',
    body: (
      <>
        <p>Subject to applicable law, you may have the right to:</p>
        <ul>
          <li>access the personal information we hold about you;</li>
          <li>request correction of inaccurate or incomplete information;</li>
          <li>request deletion of your information;</li>
          <li>object to or restrict certain processing.</li>
        </ul>
        <p>To exercise these rights, please contact us using the details below.</p>
      </>
    ),
  },
  {
    heading: '10. Children’s Privacy',
    body: (
      <p>
        Our website is not directed to children. Information about a child is collected and used only where it
        is provided by a parent or guardian for the purpose of medical care.
      </p>
    ),
  },
  {
    heading: '11. Third-Party Services',
    body: (
      <p>
        We may use third-party providers for hosting, analytics, or communications. These providers process
        information on our behalf under appropriate confidentiality and security obligations.
      </p>
    ),
  },
  {
    heading: '12. Changes to This Policy',
    body: (
      <p>
        We may update this Privacy Policy from time to time. The revised version will be posted on this page
        with the updated date. Please review it periodically.
      </p>
    ),
  },
  {
    heading: '13. Contact Us',
    body: (
      <p>
        If you have questions about this Privacy Policy or how your information is handled, please contact us
        at <a href={`mailto:${site.email}`}>{site.email}</a> or call{' '}
        <a href={site.phoneHref}>{site.phone}</a>, or visit us at {site.address}.
      </p>
    ),
  },
]

export function Privacy() {
  return (
    <LegalDocument
      title="Privacy Policy"
      subtitle="How we collect, use, and protect your personal and health information."
      lastUpdated="October 3, 2026"
      sections={sections}
    />
  )
}
